import { useState, useRef } from "react";
import { FadeIn } from "@/components/FadeIn";
import { MoveHorizontal } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const BeforeAfter = ({ before, after, label, index, t, tc }) => {
  const [pos, setPos] = useState(50);
  const ref = useRef(null);

  const move = (clientX) => {
    const rect = ref.current.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  };

  return (
    <FadeIn delay={(index % 2) * 0.1}>
      <div className="space-y-3">
        <div
          ref={ref}
          data-testid={`gallery-item-${index}`}
          className="relative h-72 sm:h-96 rounded-3xl overflow-hidden select-none cursor-ew-resize group shadow-md shadow-slate-300/40"
          onMouseMove={(e) => e.buttons === 1 && move(e.clientX)}
          onClick={(e) => move(e.clientX)}
          onTouchMove={(e) => move(e.touches[0].clientX)}
        >
          <img src={after} alt="Nachher" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
          <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
            <img src={before} alt="Vorher" className="absolute inset-0 h-full object-cover max-w-none" style={{ width: ref.current?.offsetWidth || "100%" }} draggable="false" />
            <span className="absolute top-3 left-3 bg-slate-900/80 text-white text-xs px-3 py-1 rounded-full">{t("gallery.before")}</span>
          </div>
          <span className="absolute top-3 right-3 bg-blue-600 text-white text-xs px-3 py-1 rounded-full">{t("gallery.after")}</span>
          <div className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg" style={{ left: `${pos}%` }}>
            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-xl">
              <MoveHorizontal className="w-5 h-5 text-blue-600" />
            </div>
          </div>
        </div>
        <p className="text-slate-700 text-sm font-medium pl-1">{tc(label)}</p>
      </div>
    </FadeIn>
  );
};

export const Gallery = ({ content }) => {
  const { t, tc } = useLang();
  return (
    <section id="galerie" data-testid="gallery-section" className="py-24 md:py-32 bg-white">
      <div className="container-x">
        <FadeIn className="max-w-2xl mb-14">
          <span className="overline">{t("gallery.overline")}</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mt-3 mb-4">{t("gallery.title")}</h2>
          <p className="text-slate-500 md:text-lg">{t("gallery.subtitle")}</p>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {(content.gallery || []).map((g, i) => (
            <BeforeAfter key={i} index={i} {...g} t={t} tc={tc} />
          ))}
        </div>
      </div>
    </section>
  );
};
